<template>
  <v-card elevation="0" flat>
    <BulkImportLoadingDialog
      v-model="showLoadingDialog"
      :loading="importing"
      :title="$t('memberDataSection.buyingStationOrder.bulkImport.importingTitle')"
      :message="$t('memberDataSection.buyingStationOrder.bulkImport.importingNote')"
    />

    <v-card-text style="padding: 0 !important" class="mt-3 text-body-1 black--text">
      {{ $t('memberDataSection.buyingStationOrder.bulkImport.templateNote') }}
    </v-card-text>

    <label for="po-template-select" class="mb-1 d-block text-body-2 black--text">
      {{ $t('memberDataSection.buyingStationOrder.bulkImport.selectTemplate') }}
    </label>
    <v-select
      id="po-template-select"
      v-model="selectedTemplate"
      :items="templateOptions"
      item-text="text"
      item-value="value"
      outlined
      dense
      hide-details
      style="max-width: 360px"
      class="mb-1"
    ></v-select>

    <v-btn download :href="templateDownloadUrl" color="primary" class="my-3" large :disabled="importing">
      {{ $t('memberDataSection.buyingStationOrder.bulkImport.downloadXls') }}
      <v-icon small class="ml-2">mdi-tray-arrow-down</v-icon>
    </v-btn>

    <div class="dropzone" :class="{ 'dropzone--error': showFailedState }">
      <!-- Empty / pick file -->
      <label
        v-if="!file && !showFailedState"
        class="d-flex flex-column align-center justify-center px-3 py-3"
        :class="{ active: dragging }"
        @dragenter="dragging = true"
        @dragleave="dragging = false"
        @drop.prevent="handleFileChangeEvent"
        @dragover.prevent=""
      >
        <v-icon size="52" color="info">mdi-tray-arrow-up</v-icon>
        <input ref="fileInput" class="d-none" type="file" accept=".xlsx,.xls,.csv" @change="handleFileChangeEvent" />
        <span class="font-weight-medium pt-2 text-decoration-underline blue--text darken-4">
          {{ $t('memberDataSection.buyingStationOrder.bulkImport.selectFile') }}
        </span>
        <span>{{ $t('memberDataSection.buyingStationOrder.bulkImport.dropHere') }}</span>
      </label>

      <!-- Failed: format or parse error -->
      <div v-else-if="showFailedState" class="dropzone-failed px-4 py-4">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="failed-label red--text font-weight-bold text-body-1">{{ $t('failed') }}</span>
          <v-btn icon small @click="clearFailedAndFile"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="d-flex align-center mb-2">
          <v-progress-linear rounded height="8" :value="failedProgress" color="error" background-color="#fde8e8"></v-progress-linear>
          <span class="error--text font-weight-bold ml-2">{{ failedProgress }}%</span>
        </div>
        <p class="text-body-2 black--text mb-4 text-truncate">{{ failedFileName }}</p>
        <div class="dropzone-failed-message px-4 py-5">
          <span class="dropzone-failed-message-text error--text text-body-1 font-weight-medium" :title="importErrorMessage">
            {{ importErrorMessage }}
          </span>
        </div>
      </div>

      <!-- File selected, ready to validate -->
      <div v-else class="d-flex flex-column justify-center align-center px-3 py-3" style="position: relative; width: 100%; min-height: 100%;">
        <v-icon size="52" color="info">mdi-file-document-outline</v-icon>
        <div class="mt-2 text-center"><span class="text-body-1">{{ file.name }}</span></div>
        <div class="dropzone-uploaded-message mt-3 px-4 py-2" :style="{ backgroundColor: themeColor('green2') }">
          <span class="kenyaGreen--text text-body-2">
            {{ $t('memberDataSection.buyingStationOrder.bulkImport.fileUploaded') }}
          </span>
        </div>
        <v-btn icon text class="ma-2" style="position: absolute; top: 0; right: 0;" @click="removeFile"><v-icon>mdi-close</v-icon></v-btn>
      </div>
    </div>

    <p class="pt-3 text-body-2">
      <span class="red--text">{{ $t('memberDataSection.buyingStationOrder.bulkImport.importantNote') }}</span>:
      {{ $t('memberDataSection.buyingStationOrder.bulkImport.importantNoteDetail') }}
    </p>

    <v-divider class="my-5 px-0"></v-divider>

    <v-card-actions class="px-0">
      <v-spacer></v-spacer>
      <v-btn class="mr-2" outlined color="primary" width="112" :disabled="importing" @click="cancelImport">
        {{ $t('cancel') }}
      </v-btn>
      <v-btn :disabled="!canImportFile" width="112" color="primary" class="white--text" @click="runValidation">
        {{ $t('oma.import') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapServerImport } from "@/utils/buyingStationOrderBulkImport";
import BuyingStationOrderBulkImportService from "@/_services/BuyingStationOrderBulkImportService";
import BulkImportLoadingDialog from "./BulkImportLoadingDialog.vue";

export default {
  name: "BuyingStationOrderBulkImportTab",
  components: { BulkImportLoadingDialog },
  data() {
    return {
      dragging: false,
      file: null,
      importing: false,
      showFailedState: false,
      importErrorMessage: "",
      failedFileName: "",
      failedProgress: 80,
      selectedTemplate: "xls",
      templateOptions: [
        { value: "xls", text: this.$t("memberDataSection.buyingStationOrder.bulkImport.templateXls") },
      ],
      acceptedFileExtensions: [".xls", ".xlsx", ".csv"],
    };
  },
  computed: {
    showLoadingDialog: {
      get() {
        return this.importing;
      },
      set(val) {
        if (!val && !this.importing) this.importing = false;
      },
    },
    templateDownloadUrl() {
      return `${process.env.BASE_URL || "/"}files/buying-station/purchase-order-bulk-import-template.xlsx`;
    },
    canImportFile() {
      return Boolean(this.file) && !this.showFailedState && !this.importing;
    },
  },
  methods: {
    themeColor(name) {
      const theme = this.$vuetify.theme;
      const variant = theme.dark ? "dark" : "light";
      return (theme.themes[variant] && theme.themes[variant][name]) || "";
    },
    defaultFormatErrorMessage() {
      return this.$t("memberDataSection.buyingStationOrder.bulkImport.unsupportedFileFormat");
    },
    showImportError(message, fileName, progress = 80) {
      this.showFailedState = true;
      this.importErrorMessage = message || this.defaultFormatErrorMessage();
      this.failedFileName = fileName || "";
      this.failedProgress = progress;
      this.importing = false;
    },
    clearFailedState() {
      this.showFailedState = false;
      this.importErrorMessage = "";
      this.failedFileName = "";
      this.failedProgress = 80;
    },
    clearFailedAndFile() {
      this.clearFailedState();
      this.removeFile();
    },
    isValidExtension(file) {
      const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
      return this.acceptedFileExtensions.includes(ext);
    },
    handleFileChangeEvent(event) {
      this.dragging = false;
      let files;
      if (event instanceof DragEvent) files = event.dataTransfer.files;
      else files = event.target?.files;
      if (!(files instanceof FileList) || !files.length) return;

      const picked = files[0];
      this.clearFailedState();
      if (!this.isValidExtension(picked)) {
        this.file = picked;
        this.showImportError(this.defaultFormatErrorMessage(), picked.name, 80);
        return;
      }
      this.file = picked;
    },
    removeFile() {
      this.file = null;
      this.clearFailedState();
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    cancelImport() {
      this.removeFile();
      this.$emit("cancelImport");
    },
    async runValidation() {
      if (!this.file || this.showFailedState) return;
      if (!this.isValidExtension(this.file)) {
        this.showImportError(this.defaultFormatErrorMessage(), this.file.name, 80);
        return;
      }
      this.importing = true;
      try {
        const res = await BuyingStationOrderBulkImportService.validateImport(this.file);
        if (!res || res.success === false || !res.data) {
          const msg = (res && res.message) || this.$t("memberDataSection.buyingStationOrder.bulkImport.templateErrorIntro");
          this.showImportError(msg, this.file?.name, 80);
          return;
        }
        const detail = mapServerImport(res.data);
        if (res.message) this.$notify({ type: "success", text: res.message });
        this.$emit("importSuccess", detail);
      } catch (err) {
        console.error(err);
        const msg = err?.message || this.$t("memberDataSection.buyingStationOrder.bulkImport.invalidFileFormat");
        this.showImportError(msg, this.file?.name, 80);
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.dropzone {
  height: 280px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  border: 2px dashed $gray;
  border-radius: 8px;
  background: #fff;
}
.dropzone--error {
  border-color: #e53935;
}
.dropzone label {
  cursor: pointer;
  width: 100%;
  height: 100%;
  min-height: 260px;
}
.dropzone label:hover,
.dropzone label.active {
  background-color: #f5f5f5;
}
.dropzone-failed {
  width: 100%;
  min-height: 260px;
}
.dropzone-failed-message {
  border: 1px solid #e57373;
  background-color: #ffebee;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
}
.dropzone-failed-message-text {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}
.dropzone-uploaded-message {
  border-radius: 4px;
}
</style>
